'use client';
import classNames from 'classnames';
import { CloudinaryType, TypeBlockStackedCards, TypeStackedCardItem } from '@fm-nx/contentful-types';
import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react';
import StackedCardsImage from './stacked-cards-image';

const CONTINUOUS_GRADIENT =
    'linear-gradient(169.91deg, rgb(255, 240, 171) 5.82%, rgb(243, 159, 117) 28.39%, rgb(216, 163, 203) 66.12%, rgb(184, 221, 236) 102.62%)';

const MIN_CARDS = 2;
const MAX_CARDS = 6;

type StackedCardsParallaxPropTypes = {
    blockStackedCards: TypeBlockStackedCards<'WITHOUT_UNRESOLVABLE_LINKS'>;
    numberOfCards?: number;
    mode?: 'dark' | 'light';
};

type ValidCard = TypeStackedCardItem<'WITHOUT_UNRESOLVABLE_LINKS'>;

/**
 * When fewer than 6 cards are shown, stretch the gradient so only the
 * first N/6 portion is visible (e.g. 3 cards → top 50% of the gradient).
 */
function gradientStyle(cardCount: number) {
    const heightPercent = (MAX_CARDS / cardCount) * 100;
    return {
        background: CONTINUOUS_GRADIENT,
        backgroundSize: `100% ${heightPercent}%`,
        backgroundPosition: 'top',
    };
}

export const StackedCardsParallax = (props: StackedCardsParallaxPropTypes) => {
    const { blockStackedCards, numberOfCards } = props;
    const updatedBlock = useContentfulLiveUpdates(blockStackedCards);
    const inspectorProps = useContentfulInspectorMode({ entryId: updatedBlock?.sys?.id });
    const { fields } = updatedBlock;
    const isDark = true;

    if (!fields) return null;

    const { overline, headerText, paragraph, cards } = fields;

    if (!cards || cards.length === 0) return null;

    const allValidCards = cards.filter((card): card is ValidCard => !!card && !!card.fields);

    if (allValidCards.length < MIN_CARDS) return null;

    const cardCount = numberOfCards ? Math.max(MIN_CARDS, Math.min(MAX_CARDS, numberOfCards)) : undefined;
    const validCards = cardCount ? allValidCards.slice(0, cardCount) : allValidCards;

    return (
        <section className={classNames({ 'bg-gray-900': isDark })}>
            {/* Header */}
            <div className={classNames('py-20 px-6', { 'bg-gray-900': isDark })}>
                <div className="w-full mx-auto max-w-container">
                    <div className="md:grid md:grid-cols-14 md:gap-4">
                        <div className="col-span-14 md:col-span-12 md:col-start-2">
                            <div className="flex items-start justify-center text-center gap-8">
                                <div className="max-w-2xl space-y-6">
                                    {overline && (
                                        <h2
                                            className={classNames('h5 overline', { 'text-white': isDark })}
                                            {...inspectorProps({ fieldId: 'overline' })}
                                        >
                                            {overline}
                                        </h2>
                                    )}
                                    {headerText && (
                                        <h3
                                            className={classNames('h-xl', { 'text-white': isDark })}
                                            {...inspectorProps({ fieldId: 'headerText' })}
                                        >
                                            {headerText}
                                        </h3>
                                    )}
                                    {paragraph && (
                                        <p
                                            className={classNames('p-xl', { 'text-white': isDark })}
                                            {...inspectorProps({ fieldId: 'paragraph' })}
                                        >
                                            {paragraph}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Gradient container — full width */}
            <div className="overflow-hidden rounded-[40px] md:rounded-[80px]" style={gradientStyle(validCards.length)}>
                <div className="w-full mx-auto max-w-container">
                    {validCards.map((card, index) => (
                        <StaticCard key={card.sys.id} card={card} index={index} inspectorProps={inspectorProps} />
                    ))}
                </div>
            </div>
        </section>
    );
};

type StaticCardProps = {
    card: ValidCard;
    index: number;
    inspectorProps: ReturnType<typeof useContentfulInspectorMode>;
};

const StaticCard = ({ card, index, inspectorProps }: StaticCardProps) => {
    const image =
        Array.isArray(card.fields?.image) && card.fields.image.length > 0
            ? (card.fields.image[0] as CloudinaryType)
            : null;

    return (
        <div className="px-6 py-12 md:px-24 md:py-24 lg:px-[136px] lg:py-[210px]">
            <div
                className={classNames('grid grid-cols-1 md:grid-cols-12 md:gap-8', {
                    'items-center': image,
                })}
            >
                {/* Left column: image or step number */}
                <div className="md:col-span-4 mb-6 md:mb-0">
                    {image ? (
                        <div className="select-none pointer-events-none">
                            <StackedCardsImage
                                {...inspectorProps({ fieldId: 'image' })}
                                url={image.secure_url}
                                cloudinaryId={image.public_id}
                                alt={image.context?.custom?.['alt'] || ''}
                            />
                        </div>
                    ) : (
                        <div className="select-none pointer-events-none">
                            <span
                                className="block font-bold overflow-hidden"
                                style={{
                                    fontSize: 'clamp(100px, 15vw, 240px)',
                                    lineHeight: '0.85',
                                    paddingTop: '0.12em',
                                    WebkitTextStroke: '2px rgba(0,0,0,0.85)',
                                    WebkitTextFillColor: 'transparent',
                                    color: 'transparent',
                                }}
                            >
                                {String(index + 1).padStart(2, '0')}
                            </span>
                        </div>
                    )}
                </div>

                {/* Right column: text content */}
                <div className="md:col-span-7 md:col-start-6">
                    <CardContent card={card} />
                </div>
            </div>
        </div>
    );
};

const CardContent = ({ card }: { card: ValidCard }) => {
    const updatedCard = useContentfulLiveUpdates(card);
    const cardInspectorProps = useContentfulInspectorMode({ entryId: updatedCard?.sys?.id });
    const { overline: cardOverline, headline, bodyText } = updatedCard.fields;

    return (
        <div className="space-y-4">
            {cardOverline && (
                <p className="h5 overline text-gray-900" {...cardInspectorProps({ fieldId: 'overline' })}>
                    {cardOverline}
                </p>
            )}
            {headline && (
                <h4 className="h-xl text-gray-900" {...cardInspectorProps({ fieldId: 'headline' })}>
                    {headline}
                </h4>
            )}
            {bodyText && (
                <p className="p-xl text-gray-900 mt-4" {...cardInspectorProps({ fieldId: 'bodyText' })}>
                    {bodyText}
                </p>
            )}
        </div>
    );
};

export default StackedCardsParallax;
